/**
 * Cost-Centers Service — FinOps #867 (Admin-CRUD + viewer read).
 *
 * Mirrors roomsService: admin write surface (POST / PATCH / DELETE) + viewer
 * read surface (GET list / GET by code). Reuses the shared apiClient so
 * 401/403/5xx flow through the existing global error interceptor.
 *
 * Note: the cost-center key is its `code` (== id == pk), so getById is
 * getByCode and update/delete address `/cost-centers/{code}`.
 */

import apiClient from './api';
import { ApiResponse, CostCenterDefinition } from './types';

export interface CostCentersListParams {
  /** When defined, filters by active. When omitted, returns all cost-centers. */
  active?: boolean;
}

export type CostCenterCreateInput = Omit<
  CostCenterDefinition,
  'id' | 'pk' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'
>;

// code is immutable (partition key + tag value) — never part of an update.
export type CostCenterUpdateInput = Partial<
  Omit<CostCenterDefinition, 'id' | 'pk' | 'code' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>
>;

export const costCentersApi = {
  /**
   * List cost-centers. Pass `{ active: true }` to restrict to active ones
   * (e.g. for the run-create dropdown in T4), or omit for all (admin CRUD view).
   */
  async list(params: CostCentersListParams = {}): Promise<CostCenterDefinition[]> {
    const queryParams: Record<string, string> = {};
    if (params.active !== undefined) {
      queryParams.active = String(params.active);
    }
    const response = await apiClient.get<ApiResponse<CostCenterDefinition[]>>('/cost-centers', {
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch cost-centers');
    }
    return response.data.data;
  },

  /** Fetch a single cost-center by its code. */
  async getByCode(code: string): Promise<CostCenterDefinition> {
    const response = await apiClient.get<ApiResponse<CostCenterDefinition>>(
      `/cost-centers/${encodeURIComponent(code)}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch cost-center');
    }
    return response.data.data;
  },

  /** Admin-only: create a new cost-center. */
  async create(input: CostCenterCreateInput): Promise<CostCenterDefinition> {
    const response = await apiClient.post<ApiResponse<CostCenterDefinition>>('/cost-centers', input);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create cost-center');
    }
    return response.data.data;
  },

  /**
   * Admin-only: update an existing cost-center (partial PATCH).
   * Used for both field edits and active toggling (reactivation).
   */
  async update(code: string, updates: CostCenterUpdateInput): Promise<CostCenterDefinition> {
    const response = await apiClient.patch<ApiResponse<CostCenterDefinition>>(
      `/cost-centers/${encodeURIComponent(code)}`,
      updates
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to update cost-center');
    }
    return response.data.data;
  },

  /**
   * Admin-only: soft-delete (deactivate) a cost-center.
   * The BE sets active=false — use update({ active: true }) to reactivate.
   */
  async delete(code: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<unknown>>(
      `/cost-centers/${encodeURIComponent(code)}`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to deactivate cost-center');
    }
  },
};
